'use client'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'

import type { ResolvedHeroBanner } from '@/helpers/cms/contentTypes'
import { FeatureFlags } from '@/helpers/featureFlags'
import { myAccountSettingsBase } from '@/helpers/myAccountNav'

import { BUNDLED_BANNER_STOREFRONT_LINKS } from './bundledBannerStorefronts'

type HeroBannerData = ResolvedHeroBanner

export interface HeroBannerDataProps {
  banners: HeroBannerData[]
  rotateInterval?: number
}

const useHeroBannerData = (
  /**
   * CMS banners, already ordered and flag-filtered on the server. When present they replace the
   * bundled set outright rather than merging with it — the two orderings have no relationship,
   * and interleaving them would put a banner an editor removed back on the page.
   */
  cmsBanners?: ResolvedHeroBanner[] | null,
  /**
   * Which of `BUNDLED_BANNER_STOREFRONT_LINKS` go to a hidden storefront, checked on the server.
   * CMS banners arrive already filtered.
   */
  hiddenBundledBannerLinks: readonly string[] = []
): HeroBannerDataProps => {
  const enableCostcoLaunchBanner = useFlag(
    FeatureFlags.ENABLE_COSTCO_LAUNCH_BANNER
  )

  const enableNetTermsBanner = useFlag(FeatureFlags.ENABLE_NET_TERMS_BANNER)

  const enableShopMobileBanner = useFlag(FeatureFlags.ENABLE_SHOP_MOBILE_BANNER)

  const enableAmazonPromoBanner = useFlag(
    FeatureFlags.ENABLE_AMAZON_PROMO_BANNER
  )

  const welcomeBannerVariant = useFlag(
    FeatureFlags.HOMEPAGE_WELCOME_BANNER_TEST
  )

  const myAccountHpNav = useFlag(FeatureFlags.MY_ACCOUNT_HP_NAV)

  // Define welcome banner variants for A/B test (BP-857)
  const welcomeBannerControl = {
    image:
      '/home-portal/images/heroBanners/BStockGeneric_CarouselBanner_Wide.png',
    title:
      "Welcome to the world's largest B2B marketplace for returned, excess, and trade-in inventory",
    subtitle:
      'Buy directly from top retailers and brands, across all categories, conditions, and lot sizes',
    primaryBtnText: 'Shop all listings',
    primaryBtnLink: '/all-auctions/',
  }

  const welcomeBannerTreatment = {
    image:
      '/home-portal/images/heroBanners/BStockGeneric_CarouselBanner_Wide.png',
    title: 'Source better. Scale faster.',
    subtitle:
      'Buy returned, excess, and trade-in inventory directly from top retailers and brands, across all categories, conditions, and lot sizes',
    primaryBtnText: 'Shop all listings',
    primaryBtnLink: '/all-auctions/',
  }

  // Select banner based on variant
  const welcomeBanner =
    welcomeBannerVariant === 'treatment'
      ? welcomeBannerTreatment
      : welcomeBannerControl

  const bundledBanners = [
    welcomeBanner, // Variant-based welcome banner (BP-857)
    ...(enableAmazonPromoBanner
      ? [
          {
            image:
              '/home-portal/images/heroBanners/Amazon_Homepage_CarouselBanner_120525.png',
            title: 'Introducing an Upgraded Amazon Experience',
            subtitle:
              'A fresh look and feel is here! Get to know the new platform and enjoy a more streamlined view of your orders across sellers.',
            primaryBtnText: 'Explore Amazon',
            primaryBtnLink: BUNDLED_BANNER_STOREFRONT_LINKS.amazon,
          },
        ]
      : []),
    ...(enableCostcoLaunchBanner
      ? [
          {
            image: '/home-portal/images/heroBanners/Costco_Wide.png',
            title: 'Costco. Costco Appliances. Now Combined. 🎉',
            subtitle:
              'Get to know the upgraded Costco storefront, and enjoy a more streamlined view of your orders across sellers.',
            primaryBtnText: 'Explore Costco',
            primaryBtnLink: BUNDLED_BANNER_STOREFRONT_LINKS.costco,
          },
        ]
      : []),
    ...(enableNetTermsBanner
      ? [
          {
            image:
              '/home-portal/images/heroBanners/NetTerms_Homepage_CarouselBanner_Wide.png',
            title: 'Buy Now, Pay Later with B-Stock Net Terms',
            subtitle:
              'Get 30 days to pay for your orders and unlock exclusive resale inventory only available for buyers using Net Terms.',
            primaryBtnText: 'Apply for Net Terms',
            primaryBtnLink: `${myAccountSettingsBase(myAccountHpNav)}/my/payment-method`,
          },
        ]
      : []),
    ...(enableShopMobileBanner
      ? [
          {
            image:
              '/home-portal/images/heroBanners/Mobile_Homepage_CarouselBanner_Wide.png',
            title:
              'Source Bulk Quantities of Pre-Owned Devices from Mobile Carriers and OEMs',
            subtitle:
              'Find the best deals on top-performing models by Apple, Samsung, Google, and Motorola–all in one place.',
            primaryBtnText: 'Shop Mobile',
            primaryBtnLink: '/all-auctions?categories=%5B"Cell%20Phones"%5D',
          },
        ]
      : []),
    {
      image: '/home-portal/images/heroBanners/BuyNow_CarouselBanner_Wide.png',
      title: 'Introducing Buy Now: No Bidding, Just Buying!',
      subtitle:
        'A faster way to source inventory—100+ new listings drop daily at 1pm EST from top sellers like Amazon, Target, Walmart, and Electrolux.',
      primaryBtnText: 'Shop Buy Now Listings',
      primaryBtnLink:
        '/all-auctions?o=asc&s=endTime&buyingFormat=%5B%22BUY_NOW%22%5D',
    },
    {
      image:
        '/home-portal/images/heroBanners/DirectVelocity_CarouselBanner_Wide.png',
      title: 'Contract Agreements and Direct Sales Now Available',
      subtitle:
        'Looking for a long-term sourcing solution? Lock in inventory at consistent and high volumes from top sellers on B-Stock Direct!',
      primaryBtnText: 'Shop Direct',
      primaryBtnLink: '/direct/',
    },
    {
      image:
        '/home-portal/images/heroBanners/MobileCarrier_CarouselBanner_Wide.png',
      title: 'Featured Seller: Mobile Carrier Liquidations',
      subtitle:
        'In a rush? Enjoy "Buy Now" purchasing options on the Mobile Carrier Liquidations storefront and fulfill all of your smartphone and tablet inventory needs!',
      primaryBtnText: 'Shop Mobile Carrier Inventory',
      primaryBtnLink: '/mobilecarrier/',
    },
  ]

  // A banner whose button goes to a hidden storefront would send buyers to a not found page.
  const banners = cmsBanners?.length
    ? cmsBanners
    : bundledBanners.filter(
        (banner) => !hiddenBundledBannerLinks.includes(banner.primaryBtnLink)
      )

  /**
   * A leading and trailing clone, so the carousel loops seamlessly between the real slides.
   *
   * **Not for a single banner.** Cloning one gives `[b, b, b]` — the same slide three times,
   * rotating every eight seconds between identical copies, with arrows that look broken because
   * nothing appears to change. The bundled array always has at least three, so this only comes up
   * once the CMS supplies them: an environment where exactly one banner is published and ungated.
   * `HeroBannerCarousel` reads the length back and drops the rotation and the arrows to match.
   */
  const bannersWithClones =
    banners.length > 1
      ? [
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          banners[banners.length - 1]!,
          ...banners,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          banners[0]!,
        ]
      : banners

  return {
    banners: bannersWithClones,
  }
}

export default useHeroBannerData
